import { Box, Button, Grid, Typography } from '@mui/material'
import { useFormat } from 'Services/localize/format'
import useCalculatePanel from '../payment/hook/useCalculatePanel'
import { useImperativeHandle, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import MonetaryFormat from 'components/MonetaryFormat/MonetaryFormat'
import { isUndefined } from 'lodash-es'
import { CustomerPaymentAccountDetailContract } from 'models'
import GroupIcon from '@mui/icons-material/Group'
import { PAYMENT_TYPE_ID } from 'constant/paymentType'
import {
    QuickButton,
    TextBox,
    textFontStyle,
    TotalAmountBox,
    RedeemButton,
} from './CalculatePanelUI'
import { useScreenSize } from 'hooks/useScreenSize/useScreenSize'
import NumberPad from '../../NumberPad/NumberPad'
import { useMobileOrientation } from 'react-device-detect'
import PaymentAccountDetail from '../payment/PaymentAccountDetail'

export enum CalculatorSwitchMode {
    Percentage = 'PERCENTAGE',
    Custom = 'CUSTOM',
}

export interface IPaymentTypeInfo {
    paymentType?: number
    isCutomPayment?: boolean
    paymentAccount?: CustomerPaymentAccountDetailContract
}
export interface CalculatePanelProps {
    initialValue?: number
    quickButtons?: number[]
    onChange: (total: number) => void

    /**
     * Hide Dot CalculatePanel
     */
    hideDot?: boolean

    /**
     * Hide Quick Buttons
     */
    hideQuickButtons?: boolean

    /**
     * Custom Total CalculatePanel
     */
    totalAmount?: number
    disableKeyboard?: boolean
    paymentDetail?: IPaymentTypeInfo
    onChangeCustomerAccount?: () => void
    /**
     * Hide + - and =
     */
    hideOperators?: boolean
    /**
     * Switch mode % or value
     */
    showSwitchCalculatorMode?: boolean
    onChangeCalculatorSwitchMode?: (calMode: CalculatorSwitchMode) => void
    calculatorSwitchMode?: CalculatorSwitchMode
    useRedeemPointButton?: boolean
    disabledRedeemPoint?: boolean
    isRedeemPointMode?: boolean
    onClickRedeemPoint?: (selected: boolean) => void
    isNotMoneyValue?: boolean
    maxValue?: number
    minValue?: number
    disableSwitchMode?: boolean
}

export type CalculatePanelPropsHandle = {
    resetForm: () => void
    setTotal: (text: string) => void
}

const CalculatePanel = forwardRef<
    CalculatePanelPropsHandle,
    CalculatePanelProps
>((props: CalculatePanelProps, ref) => {
    const { currency, currencySymbol } = useFormat()
    const { t } = useTranslation()
    const {
        paymentDetail,
        onChangeCustomerAccount,
        showSwitchCalculatorMode = false,
        onChangeCalculatorSwitchMode,
        calculatorSwitchMode = CalculatorSwitchMode.Percentage,
        useRedeemPointButton = false,
        onClickRedeemPoint,
        disabledRedeemPoint,
        isRedeemPointMode,
        isNotMoneyValue,
        hideOperators = false,
        disableKeyboard = false,
        disableSwitchMode = false,
    } = props

    const {
        text,
        quickButtonFormat,
        onClickQuickButton,
        onClickNumPad,
        onClear,
        hideDot,
        totalAmount,
        setTotal,
        hideQuickButtons,
    } = useCalculatePanel(props)

    useImperativeHandle(ref, () => ({
        resetForm: () => onClear(),
        setTotal: (text: string) => setTotal(text),
    }))

    const disableNumpad =
        !isUndefined(paymentDetail?.paymentType) &&
        paymentDetail?.paymentType !== PAYMENT_TYPE_ID.CASH &&
        paymentDetail?.paymentType !== PAYMENT_TYPE_ID.TIPS
    const { isMediumScreen, isMobile } = useScreenSize()
    const { isPortrait } = useMobileOrientation()
    const hideNumpad = disableNumpad && isMediumScreen
    return (
        <Grid
            container
            direction="column"
            flexWrap={'nowrap'}
            sx={{
                backgroundColor: isMobile
                    ? 'background.paper'
                    : 'background.default',
                height: '100%',
            }}
        >
            {hideDot ? (
                <Grid container direction="row" alignItems="stretch">
                    <TotalAmountBox container item>
                        <Grid item xs={6}>
                            <Typography
                                sx={textFontStyle}
                                style={{ textTransform: 'uppercase' }}
                                color="primary"
                                align="left"
                            >
                                {t('pos_terminal.message.total_amount')}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography
                                sx={textFontStyle}
                                align="right"
                                color="text"
                            >
                                <MonetaryFormat
                                    value={totalAmount || 0}
                                    forceMonetaryFormat="plus"
                                    isOnlyString
                                />{' '}
                                {showSwitchCalculatorMode &&
                                calculatorSwitchMode ===
                                    CalculatorSwitchMode.Percentage
                                    ? '%'
                                    : ''}
                            </Typography>
                        </Grid>
                    </TotalAmountBox>
                </Grid>
            ) : (
                <TextBox item>
                    <Typography
                        sx={{
                            marginTop: { xs: '8px', lg: '10px' },
                            marginRight: { xs: '16px', lg: '20px' },
                            ...textFontStyle,
                        }}
                    >
                        {text !== ''
                            ? showSwitchCalculatorMode &&
                              calculatorSwitchMode ===
                                  CalculatorSwitchMode.Percentage
                                ? text + '%'
                                : isNotMoneyValue
                                ? text
                                : text + currencySymbol
                            : currency(0)}
                    </Typography>
                </TextBox>
            )}
            {!hideQuickButtons && (
                <Grid item container direction="column">
                    <Grid item style={{ margin: 10 }}>
                        {isRedeemPointMode ? (
                            <Typography color="primary">
                                Quick points to redeem
                            </Typography>
                        ) : paymentDetail?.paymentType ===
                          PAYMENT_TYPE_ID.CUSTOMER_ACCOUNT ? (
                            <span>
                                <Grid
                                    container
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                >
                                    <Grid item>
                                        <Typography color="primary">
                                            {paymentDetail?.paymentAccount ? (
                                                <PaymentAccountDetail
                                                    paymentAccountName={
                                                        paymentDetail
                                                            ?.paymentAccount
                                                            ?.name ?? ''
                                                    }
                                                    paymentAccountBalance={
                                                        paymentDetail
                                                            .paymentAccount
                                                            ?.accountBalance ??
                                                        0
                                                    }
                                                />
                                            ) : (
                                                'Please select Account'
                                            )}
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Button
                                            size="small"
                                            color="primary"
                                            startIcon={<GroupIcon />}
                                            onClick={onChangeCustomerAccount}
                                        >
                                            {paymentDetail?.paymentAccount
                                                ? t(
                                                      'quick_sell.label.change_account',
                                                  )
                                                : 'Select Account'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </span>
                        ) : paymentDetail?.paymentType ===
                              PAYMENT_TYPE_ID.CASH &&
                          !paymentDetail?.isCutomPayment ? (
                            <Typography color="primary">
                                {t('quick_sell.label.quick_cash_payment')}
                            </Typography>
                        ) : paymentDetail?.paymentType ===
                          PAYMENT_TYPE_ID.TIPS ? (
                            <Typography color="primary">
                                {t('quick_sell.label.tip_amount')}
                            </Typography>
                        ) : (
                            <Typography color="primary">
                                {t('quick_sell.label.quick_payment')}
                            </Typography>
                        )}
                    </Grid>
                    <Grid item container style={{ margin: 10 }}>
                        {quickButtonFormat
                            .filter((f) => !f.includes('NaN'))
                            .map((val, index) => {
                                return (
                                    <Grid key={'quick-button-cal-' + index}>
                                        <QuickButton
                                            disabled={
                                                disableNumpad && index > 0
                                            }
                                            key={`quick-button-${val}`}
                                            onClick={() =>
                                                onClickQuickButton(val)
                                            }
                                        >
                                            {showSwitchCalculatorMode &&
                                            calculatorSwitchMode ===
                                                CalculatorSwitchMode.Percentage
                                                ? val + '%'
                                                : isRedeemPointMode
                                                ? Number(val).toFixed(2)
                                                : currency(
                                                      val || 0,
                                                      undefined,
                                                      {
                                                          fraction: false,
                                                          isAccounting: false,
                                                      },
                                                  )}
                                        </QuickButton>
                                    </Grid>
                                )
                            })}
                    </Grid>
                </Grid>
            )}
            {useRedeemPointButton && (
                <Grid item container>
                    <RedeemButton
                        sx={{
                            backgroundColor: isRedeemPointMode
                                ? 'secondary.main'
                                : 'default.dark',
                            color: isRedeemPointMode
                                ? 'white'
                                : 'default.contrastText',
                        }}
                        onClick={() => {
                            onClickRedeemPoint?.(!isRedeemPointMode)
                        }}
                        disabled={disabledRedeemPoint}
                    >
                        {isRedeemPointMode
                            ? 'Back to payment'
                            : t('finish.button.redeem_points')}
                    </RedeemButton>
                </Grid>
            )}
            {!hideNumpad && (
                <Grid item container direction="column" xs>
                    <Box
                        display="flex"
                        flexDirection="column"
                        marginRight="4px"
                        marginLeft={'2px'}
                        sx={{
                            minHeight:
                                isPortrait && isMobile ? '320px' : undefined,
                            height: '100%',
                        }}
                    >
                        <NumberPad
                            onClick={onClickNumPad}
                            hideQuickButtons
                            disableSwitchMode={disableSwitchMode}
                            onSwitchMode={onChangeCalculatorSwitchMode}
                            currentSwitchMode={calculatorSwitchMode}
                            isCalculatorMode={!hideOperators}
                            hideDot={hideDot}
                            disabled={disableNumpad}
                            disableKeyboard={disableKeyboard}
                        />
                    </Box>
                </Grid>
            )}
        </Grid>
    )
})

export default CalculatePanel
